import { useCallback, useEffect, useMemo, useState } from 'react';
import type { RefObject } from 'react';
import type { StoryData } from 'storyblok-js-client';
import type { ArticleHeaderBlokData } from '../../organisms/article-header/ArticleHeader.data';
import { setGridHeight } from './NewsOverview.transitions';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import { fadeFromTo, fadeTo } from '../../../util/animation/fade/fade';
import { useGlobalStore } from '../../../stores/global';
import type { NewsOverviewBlokData } from './NewsOverview.data';
import { useMobileViewport } from '../../../hooks/useMobileViewport';
import sharedVariables from '../../../data/shared-variable/shared-variables.json';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import type { ScrollProviderContextType } from '../../unlisted/ScrollProvider';
import { useScrollProviderContext } from '../../unlisted/ScrollProvider';
import { useNextQueryParam } from '../../../hooks/useNextQueryParam';
import { useRouter } from 'next/router';
import useRouterPush from '../../../hooks/useRouterPush';

export function useNewsFiltering(
  elementRef: RefObject<HTMLDivElement | null>,
  gridRef: RefObject<HTMLDivElement | null>,
  blok: NewsOverviewBlokData,
): {
  onFilterClick: (filter: string, type: string) => Promise<void>;
  activeFilter: string;
  activeCountryFilter: string | undefined;
  newsItems: Array<StoryData<ArticleHeaderBlokData>> | undefined;
  totalPages: number;
  pageIndex: number;
  onPaginationClick: (value: number) => void;
  showNoResultMessage: boolean;
  resetFilters: () => void;
} {
  const initialPageIndex = +(useNextQueryParam('page') || 1) - 1;
  const initialCategory = useNextQueryParam('category') || blok.news_category_all;
  const sanitizedInitialCategory = initialCategory
    .replace(/[-_+#]/g, ' ')
    .trim()
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const isMobileViewport = useMobileViewport(sharedVariables.deviceState.MIN_768, false);
  const store = useGlobalStore();
  const { recentNews, featuredNews } = store;
  const [activeFilter, setActiveFilter] = useState(sanitizedInitialCategory);
  const [activeCountryFilter, setActiveCountryFilter] = useState(blok.news_country_all);
  const [newsItems, setNewsItems] = useState<Array<StoryData<ArticleHeaderBlokData>> | undefined>();
  const [pageIndex, setPageIndex] = useState<number>(initialPageIndex);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [showNoResultMessage, setShowNoResultMessage] = useState(false);

  const scrollContext = useScrollProviderContext() as ScrollProviderContextType;

  const perPage = useMemo(() => {
    if (isMobileViewport) {
      return +blok.mobile_items_per_page;
    }
    return +blok.desktop_items_per_page;
  }, [blok.desktop_items_per_page, blok.mobile_items_per_page, isMobileViewport]);

  const calculateTotalPages = useCallback(
    (items: Array<StoryData<ArticleHeaderBlokData>> | undefined) =>
      Math.ceil((items?.length || 0) / perPage),
    [perPage],
  );

  const router = useRouter();
  const routerPush = useRouterPush();

  const setPageIndexAndSlug = useCallback(
    (index: number): void => {
      setPageIndex(index);
      const query = { ...router.query, page: index + 1 };
      if (index === 0) {
        // @ts-ignore
        delete query.page;
      }

      routerPush(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: true }, // prevents full page reload
      );
    },
    [router.pathname, router.query, routerPush],
  );

  const setCategoryAndSlug = useCallback(
    (category: string): void => {
      setActiveFilter(category);
      const query = { ...router.query, category };
      if (category === blok.news_category_all) {
        // @ts-ignore
        delete query.category;
      }
      // @ts-ignore
      delete query.page;

      routerPush(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: true }, // prevents full page reload
      );
    },
    [blok.news_category_all, router.pathname, router.query, routerPush],
  );

  useEffect(() => {
    let filteredNews;

    // Filter newsItems based on Category
    if (activeFilter === blok.news_category_all) {
      filteredNews = recentNews?.filter(
        (item) => !featuredNews?.map((news) => news.news.slug).includes(item.slug),
      );
    } else {
      filteredNews = recentNews?.filter((item) => item.content.category === activeFilter);
    }

    // Filter newsItems based on Location
    if (activeCountryFilter !== blok.news_country_all) {
      filteredNews = filteredNews?.filter((item) => item.content.location === activeCountryFilter);
    }
    // else {
    //   filteredNews = filteredNews?.filter((item) => !item.content.location);
    // }

    // Filter newsItems based on currentPageIndex
    const startItems = pageIndex * perPage;
    const currentPageNews = filteredNews?.slice(startItems, startItems + perPage);
    setNewsItems(currentPageNews);
    setShowNoResultMessage(currentPageNews?.length === 0);

    setTotalPages(calculateTotalPages(filteredNews));

    // Update/Tween the grid-height to the new size
    (async () => {
      await setGridHeight(gridRef, true);
      ScrollTrigger.refresh();
    })();
  }, [
    featuredNews,
    activeFilter,
    recentNews,
    blok.news_category_all,
    activeCountryFilter,
    blok.news_country_all,
    pageIndex,
    perPage,
    calculateTotalPages,
    gridRef,
  ]);

  const hideGrid = useCallback(async () => {
    setGridHeight(gridRef, false);
    if (gridRef.current) {
      await fadeTo(gridRef.current, { duration: 0.2 });
    }
  }, [gridRef]);

  const showGrid = useCallback(() => {
    if (gridRef.current) {
      fadeFromTo(gridRef.current, { duration: 0.2 });
    }
  }, [gridRef]);

  const resetFilters = useCallback(async () => {
    setCategoryAndSlug(blok.news_category_all);
    setActiveCountryFilter(blok.news_country_all);
  }, [blok.news_category_all, blok.news_country_all, setCategoryAndSlug]);

  const onPaginationClick = useCallback(
    async (value: number) => {
      if (value !== pageIndex) {
        await hideGrid();
        setPageIndexAndSlug(value);
        showGrid();
      }
    },
    [hideGrid, pageIndex, setPageIndexAndSlug, showGrid],
  );

  async function onFilterClick(filter: string, type: string): Promise<void> {
    // Get the previousFilter for the clicked filter(type)
    let previousFilter;
    if (type === blok.news_category_all) {
      previousFilter = activeFilter;
    } else if (type === blok.news_country_all) {
      previousFilter = activeCountryFilter;
    }

    // Only fade-out grid and remove all items to re-trigger the news-items animation when a filter has changed
    if (filter !== previousFilter) {
      await hideGrid();
      setNewsItems([]);
      setPageIndex(0);
      showGrid();
    }

    // Update the correct clicked filter(type)
    if (type === blok.news_category_all) {
      setCategoryAndSlug(filter);
    } else if (type === blok.news_country_all) {
      setActiveCountryFilter(filter);
    }
  }

  useUpdateEffect(() => {
    const rect = elementRef.current?.getBoundingClientRect();
    const currentY = scrollContext.scrollStatusRef.current?.offset.y || 0;
    scrollContext.scrollTo(0, (rect?.top || 0) + currentY - 100, 600);
  }, [pageIndex]);

  useEffect(() => {
    ScrollTrigger.refresh();
  }, [newsItems]);

  return {
    onFilterClick,
    activeFilter,
    activeCountryFilter,
    newsItems,
    totalPages,
    pageIndex,
    onPaginationClick,
    showNoResultMessage,
    resetFilters,
  };
}
